import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileSpreadsheet, FileText, Coins, Download, MessageCircle, TrendingDown, Wallet, AlertOctagon, LineChart as LineChartIcon, Lock, Unlock, Eye, Wrench, X, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
// Update 21: WhatsApp karne paylaşımı kaldırıldı.
import { formatApiErrorDetail } from "@/lib/api";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import { useAuth } from "@/context/AuthContext";
import { useVehicleMap, brandOf, vehicleLabel } from "@/lib/vehicles";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Bonuses() {
  const { user } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const vmap = useVehicleMap();
  const [rows, setRows] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [pool, setPool] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [trend, setTrend] = useState([]);
  const [lockInfo, setLockInfo] = useState({ locked: false, lock: null });
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockNote, setLockNote] = useState("");
  const [lockBusy, setLockBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  // Manuel havuz ayarlamaları
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjMechId, setAdjMechId] = useState("");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjDesc, setAdjDesc] = useState("");
  const [adjDate, setAdjDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [adjustments, setAdjustments] = useState([]);
  const [voidingId, setVoidingId] = useState(null);

  const load = async (m) => {
    const [a, mech, p, lk, adj] = await Promise.all([
      api.get("/analytics/mechanics", { params: { month: m } }),
      api.get("/mechanics"),
      api.get("/analytics/pool", { params: { month: m } }),
      api.get(`/period-locks/${m}`).catch(() => ({ data: { locked: false, lock: null } })),
      api.get("/pool-adjustments", { params: { month: m } }).catch(() => ({ data: { items: [] } })),
    ]);
    setRows(a.data);
    setMechanics(mech.data);
    setPool(p.data);
    setLockInfo(lk.data || { locked: false, lock: null });
    setAdjustments(adj.data?.items || []);
  };
  const loadTrend = async (y) => {
    const { data } = await api.get("/analytics/pool-timeseries", { params: { year: y } });
    setTrend(data.map((d) => ({ ...d, monthShort: d.month.slice(5) })));
  };
  useEffect(() => { load(month); }, [month]);
  useEffect(() => { loadTrend(year); }, [year]);

  const token = localStorage.getItem("access_token");

  const download = async (fmt) => {
    // Update 21: httpOnly access_token cookie'siyle açılır — ?auth= kaldırıldı
    const url = `${API}/export/bonuses.${fmt}?month=${month}`;
    window.open(url, "_blank");
  };

  const downloadKarne = (mid) => {
    const url = `${API}/mechanics/${mid}/karne.pdf?month=${month}&_ts=${Date.now()}`;
    window.open(url, "_blank");
  };

  const previewKarne = (row) => {
    const url = `${API}/mechanics/${row.mechanic_id}/karne.pdf?month=${month}`;
    setPreviewUrl(url);
    setPreviewTitle(`${row.name} — Karne (${month})`);
  };

  const doLock = async () => {
    setLockBusy(true);
    try {
      await api.post(`/period-locks/${month}/lock`, { note: lockNote.trim() || null });
      toast.success(`${month} dönemi kilitlendi`);
      setLockDialogOpen(false);
      setLockNote("");
      await load(month);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kilitleme başarısız");
    } finally {
      setLockBusy(false);
    }
  };

  const doUnlock = async () => {
    if (!confirm(`${month} dönemi kilidi kaldırılsın mı?\n\nBu dönemin iş emirleri tekrar düzenlenebilir hale gelir; prim hesabı yeniden hesaplanabilir.`)) return;
    setLockBusy(true);
    try {
      await api.post(`/period-locks/${month}/unlock`, {});
      toast.success(`${month} dönemi kilidi kaldırıldı`);
      await load(month);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kilit kaldırılamadı");
    } finally {
      setLockBusy(false);
    }
  };

  // Update 21: WhatsApp karne paylaşımı kaldırıldı. Karneler yalnız portal
  // içinden görüntülenir/indirilir.

  const budget = pool?.budget || 50000;
  const deductions = pool?.deductions_amount || 0;
  const netPool = pool?.net_pool || 50000;
  const deductionsCount = pool?.deductions_count || 0;
  const spendPct = budget > 0 ? (deductions / budget) * 100 : 0;
  const manualDeductions = pool?.manual_deductions_amount || 0;
  const autoDeductions = pool?.auto_deductions_amount || 0;

  const openAdjModal = () => {
    setAdjMechId(mechanics[0]?.id || "");
    setAdjAmount("");
    setAdjDesc("");
    setAdjDate(new Date().toISOString().slice(0, 10));
    setAdjOpen(true);
  };

  const submitAdjustment = async () => {
    const amount = parseFloat(String(adjAmount).replace(",", "."));
    if (!adjMechId) { toast.error("Usta seçin"); return; }
    if (!amount || amount <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    if (!adjDesc.trim()) { toast.error("Açıklama zorunlu"); return; }
    setAdjBusy(true);
    try {
      await api.post("/pool-adjustments", {
        mechanic_id: adjMechId, month, amount_tl: amount,
        description: adjDesc.trim(), date: adjDate,
      });
      toast.success("Manuel havuz kesintisi eklendi");
      setAdjOpen(false);
      load(month);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    } finally { setAdjBusy(false); }
  };

  const voidAdjustment = async (id) => {
    if (!window.confirm("Bu manuel kesintiyi iptal etmek istediğinize emin misiniz? Tutar havuza geri döner.")) return;
    setVoidingId(id);
    try {
      await api.delete(`/pool-adjustments/${id}`);
      toast.success("İşlem iptal edildi, havuza geri döndü");
      load(month);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İptal edilemedi");
    } finally { setVoidingId(null); }
  };

  return (
    <div className="space-y-6" data-testid="bonuses-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Aylık Havuz Raporu</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Prim Havuzu & Karneler</h1>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Dönem</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1" data-testid="month-picker" />
          </div>
          <Button onClick={() => download("xlsx")} data-testid="export-xlsx-btn" variant="outline">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button onClick={() => download("pdf")} data-testid="export-pdf-btn" className="bg-[#1E3A8A] hover:bg-[#1e40af]">
            <FileText className="w-4 h-4 mr-2" /> PDF (Toplu)
          </Button>
          {user?.role === "admin" && (
            <Button
              onClick={openAdjModal}
              className="bg-orange-600 hover:bg-orange-700 text-white shadow-md"
              data-testid="manual-adj-btn"
              title={lockInfo.locked
                ? "Havuzdan manuel mesai/avans düşme (kilitli aya da yönetici olarak girebilirsiniz)"
                : "Havuzdan manuel mesai/avans düşme"}
            >
              <Wrench className="w-4 h-4 mr-2" /> Manuel Mesai Düş / Ekle
              {lockInfo.locked && <Lock className="w-3 h-3 ml-1.5 opacity-70" />}
            </Button>
          )}
          {user?.role === "admin" && (
            lockInfo.locked ? (
              <Button onClick={doUnlock} disabled={lockBusy} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="unlock-month-btn">
                <Unlock className="w-4 h-4 mr-2" /> Kilidi Kaldır
              </Button>
            ) : (
              <Button onClick={() => setLockDialogOpen(true)} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="lock-month-btn">
                <Lock className="w-4 h-4 mr-2" /> Ayı Kilitle
              </Button>
            )
          )}
        </div>
      </div>

      {/* Lock banner */}
      {lockInfo.locked && lockInfo.lock && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 flex items-start gap-3" data-testid="lock-banner">
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-amber-900">Bu Dönem Kilitli — Prim Ödenmiş</div>
            <div className="text-sm text-amber-800 mt-0.5">
              {month} dönemi <b>{lockInfo.lock.locked_by_name}</b> tarafından{" "}
              <span className="font-mono">{new Date(lockInfo.lock.locked_at).toLocaleString("tr-TR")}</span> tarihinde kilitlendi.
              Bu döneme ait iş emirleri düzenlenemez, silinemez veya yenisi eklenemez. Aşağıdaki rakamlar kilitleme anındaki değerlerdir.
            </div>
            {lockInfo.lock.note && (
              <div className="mt-2 text-xs text-amber-900 bg-white/60 border border-amber-200 rounded px-2 py-1 inline-block">
                <span className="text-slate-500 mr-1">Not:</span>{lockInfo.lock.note}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pool status hero card */}
      <div className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] rounded-lg text-white overflow-hidden" data-testid="pool-hero">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-0">
          <div className="p-6 lg:col-span-2 border-r border-white/10">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-blue-300 font-mono">
              <Wallet className="w-3 h-3" /> Ortak Prim Havuzu · {month}
            </div>
            <div className="mt-2 text-5xl lg:text-6xl font-bold tracking-tight metric-num">
              ₺{netPool.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <div className="text-xs text-blue-200 mt-1 font-mono">
              Toplam Bütçe: ₺{budget.toLocaleString("tr-TR")} — Kalan Dağıtılabilir
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-red-500 transition-all" style={{ width: `${Math.min(100, spendPct)}%` }} />
            </div>
            <div className="flex justify-between mt-1 text-[10px] font-mono text-blue-200">
              <span>%{spendPct.toFixed(1)} kesildi</span>
              <span>%{(100 - spendPct).toFixed(1)} kaldı</span>
            </div>
          </div>
          <div className="p-6 border-r border-white/10">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-red-300 font-mono">
              <TrendingDown className="w-3 h-3" /> Havuz Kesintisi
            </div>
            <div className="mt-2 text-3xl font-bold metric-num text-red-400">
              -₺{deductions.toLocaleString("tr-TR")}
            </div>
            <div className="text-xs text-blue-200 mt-1 font-mono" data-testid="pool-deduction-breakdown">
              {pool?.deduction_display || `Otomatik: ₺${autoDeductions.toLocaleString("tr-TR")} (${deductionsCount} olay)`}
            </div>
            {manualDeductions > 0 && (
              <div className="text-xs text-orange-300 mt-0.5 font-mono" data-testid="manual-deduction-summary">
                Manuel: ₺{manualDeductions.toLocaleString("tr-TR")} ({pool?.manual_deductions_count || 0} işlem)
              </div>
            )}
          </div>
          <div className="p-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-blue-300 font-mono">
              <Coins className="w-3 h-3" /> Toplam Puan
            </div>
            <div className="mt-2 text-3xl font-bold metric-num">
              {(pool?.total_points || 0).toLocaleString("tr-TR")}
            </div>
            <div className="text-xs text-blue-200 mt-1 font-mono">
              Ustalar bu puanların oranında pay alır
            </div>
          </div>
        </div>
      </div>

      {/* Deductions log */}
      {deductionsCount > 0 && (
        <Card className="border-red-200 shadow-none" data-testid="deductions-log">
          <CardHeader className="border-b border-red-100 bg-red-50/40">
            <div className="flex items-center gap-2">
              <AlertOctagon className="w-4 h-4 text-red-600" />
              <CardTitle className="font-heading text-base text-red-900">Havuz Kesinti Detayı ({deductionsCount} olay)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider">Tarih</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Araç</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Ana Usta</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Neden</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Kesinti</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pool.deduction_events.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{e.date?.slice(0, 10)}</TableCell>
                      <TableCell className="font-semibold">
                        {e.vehicle_code && <span className="font-mono text-[#1E3A8A] mr-1">{e.vehicle_code}</span>}
                        {vehicleLabel("", e.plate, brandOf(vmap, e.plate))}
                      </TableCell>
                      <TableCell>{e.primary_mechanic_name}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-md truncate">{e.fault_reported}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-red-600">-₺{e.amount.toLocaleString("tr-TR")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manuel havuz işlem geçmişi */}
      {adjustments.length > 0 && (
        <Card className="border-orange-200 shadow-none" data-testid="manual-adj-log">
          <CardHeader className="border-b border-orange-100 bg-orange-50/40">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-orange-600" />
              <CardTitle className="font-heading text-base text-orange-900">
                Manuel Havuz İşlem Geçmişi ({adjustments.length})
              </CardTitle>
              <span className="ml-auto text-xs font-mono text-orange-800">
                Toplam: -₺{adjustments.reduce((s, a) => s + (a.amount_tl || 0), 0).toLocaleString("tr-TR")}
              </span>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider">Tarih</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Usta</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Açıklama</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">İşlemi Yapan</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Tutar</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Aksiyon</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adjustments.map((a) => (
                    <TableRow key={a.id} data-testid={`manual-adj-row-${a.id}`}>
                      <TableCell className="font-mono text-xs">{a.date}</TableCell>
                      <TableCell className="font-semibold">{a.mechanic_name}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-md">{a.description}</TableCell>
                      <TableCell className="text-xs text-slate-500">{a.created_by_name}</TableCell>
                      <TableCell className="text-right font-mono font-bold text-orange-700">
                        -₺{(a.amount_tl || 0).toLocaleString("tr-TR")}
                      </TableCell>
                      <TableCell className="text-right">
                        {user?.role === "admin" && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => voidAdjustment(a.id)}
                            disabled={voidingId === a.id}
                            className="text-slate-500 hover:text-red-600"
                            data-testid={`manual-adj-void-${a.id}`}
                            title={lockInfo.locked
                              ? "Kilitli aya ait bu işlemi yönetici olarak iptal et"
                              : "Bu işlemi iptal et (havuza geri döner)"}
                          >
                            {voidingId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100"><CardTitle className="font-heading text-lg">Usta Bazlı Havuz Dağılımı ({month})</CardTitle></CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">Usta</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">İş</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Ort. Süre</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Başarı</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Tekrar</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Puan</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Pay %</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Hakediş (TL)</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Karne</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.mechanic_id} className="hover:bg-slate-50" data-testid={`bonus-row-${r.mechanic_id}`}>
                    <TableCell className="font-semibold">{r.name}</TableCell>
                    <TableCell className="text-right font-mono">{r.job_count}</TableCell>
                    <TableCell className="text-right font-mono">{r.avg_duration_minutes}dk</TableCell>
                    <TableCell className="text-right font-mono">%{r.success_rate}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{r.repeat_faults}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{r.total_points}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-14 h-1.5 rounded bg-slate-100 overflow-hidden">
                          <div className="h-full bg-[#1E3A8A]" style={{ width: `${Math.min(100, r.share_percent || 0)}%` }} />
                        </div>
                        <span className="font-mono text-xs w-12 text-right">%{r.share_percent}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-[#1E3A8A]" data-testid={`bonus-total-tl-${r.mechanic_id}`}>
                      ₺{r.total_tl.toLocaleString("tr-TR")}
                      {(r.manual_tl || 0) > 0 && (
                        <div className="text-[10px] font-normal text-orange-700 mt-0.5" title="Manuel ek ödeme">
                          +₺{r.manual_tl.toLocaleString("tr-TR")} manuel
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => previewKarne(r)} title="Karne Önizle" data-testid={`karne-preview-${r.mechanic_id}`}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadKarne(r.mechanic_id)} title="Karne PDF İndir" data-testid={`karne-pdf-${r.mechanic_id}`}>
                        <Download className="w-4 h-4 text-[#1E3A8A]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Bu dönemde kayıt yok</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Lock confirmation dialog */}
      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent data-testid="lock-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" /> {month} Dönemini Kilitle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              Bu dönemin priminin ödendiğini onayladığınızda, <b>{month}</b> ayına ait iş emirleri:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-600 ml-2">
              <li>Yeni <b>iş emri eklenemez</b></li>
              <li>Mevcut kayıtlar <b>düzenlenemez / silinemez</b></li>
              <li>Ödenen prim tutarları anlık olarak <b>sabitlenir</b> (snapshot)</li>
            </ul>
            <p className="text-xs text-slate-500">Kilit her zaman admin tarafından kaldırılabilir.</p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Not (opsiyonel)</Label>
              <Textarea
                value={lockNote}
                onChange={(e) => setLockNote(e.target.value)}
                placeholder="Örn: Prim 05.08.2026 tarihinde ödendi, banka dekontu klasörde."
                rows={2}
                data-testid="lock-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialogOpen(false)}>Vazgeç</Button>
            <Button onClick={doLock} disabled={lockBusy} className="bg-amber-600 hover:bg-amber-700" data-testid="lock-confirm-btn">
              <Lock className="w-4 h-4 mr-2" /> Evet, Kilitle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manuel havuz kesintisi/ek ödeme modalı */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="max-w-md" data-testid="manual-adj-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-600" /> Havuzdan Manuel Mesai Düş / Ek Ödeme
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-orange-50 border border-orange-200 rounded p-3 text-xs text-orange-900">
              <b>Not:</b> Bu işlem şoför kusuru olmaksızın havuzdan doğrudan düşer ve seçilen ustaya
              "Manuel Ek Ödeme" olarak yansır. Şoför cari hesabına ceza <b>yazılmaz</b>.
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Usta / Personel *</Label>
              <select
                value={adjMechId}
                onChange={(e) => setAdjMechId(e.target.value)}
                className="mt-1 w-full h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
                data-testid="manual-adj-mech-select"
              >
                <option value="">— Usta Seçin —</option>
                {mechanics.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-600">Dönem</Label>
                <Input value={month} readOnly className="mt-1 bg-slate-50 font-mono" data-testid="manual-adj-month" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-600">İşlem Tarihi</Label>
                <Input type="date" value={adjDate} onChange={(e) => setAdjDate(e.target.value)} className="mt-1" data-testid="manual-adj-date" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Tutar (₺) *</Label>
              <Input
                type="number" min="0" step="0.01" inputMode="decimal"
                value={adjAmount}
                onChange={(e) => setAdjAmount(e.target.value)}
                placeholder="Örn: 1500"
                className="mt-1 font-mono text-lg"
                data-testid="manual-adj-amount"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Açıklama *</Label>
              <Textarea
                value={adjDesc}
                onChange={(e) => setAdjDesc(e.target.value)}
                placeholder="Örn: Ağustos ayı 9 adet mesai karşılığı havuz payı mahsubu"
                rows={3}
                className="mt-1"
                data-testid="manual-adj-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)} disabled={adjBusy}>Vazgeç</Button>
            <Button
              onClick={submitAdjustment}
              disabled={adjBusy || !adjMechId || !adjAmount || !adjDesc.trim()}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="manual-adj-submit"
            >
              {adjBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
              Havuzdan Düş / Ödeme Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Karne PDF preview modal */}
      <PdfPreviewModal
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
        title={previewTitle}
        url={previewUrl}
      />
    </div>
  );
}

function SeasonBox({ label, value, color }) {
  const colorMap = {
    red: "border-l-red-500 text-red-700",
    amber: "border-l-amber-500 text-amber-700",
    blue: "border-l-[#1E3A8A] text-[#1E3A8A]",
  };
  return (
    <div className={`bg-white border border-slate-200 border-l-4 rounded-md p-4 ${colorMap[color]}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className="metric-num text-xl font-bold mt-1">{value}</div>
    </div>
  );
}
